import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import Title from '../components/global/title';
import { Spinner } from '@blueprintjs/core';
import NotLoggedIn from '../components/home/notloggedin/notloggedin';
import GameGamesList from '../components/game/gameslist';
import HomeGamesHeaderCard from '../components/home/gamesheadercard';
import PullToRefresh from '../components/global/pulltorefresh';
import { useActiveGamesProvider } from '../components/global/activegamesprovider';
import HomeLiveGames from '../components/home/livegames';
import { getServerSession } from 'next-auth';
import { authOptions } from './api/auth/[...nextauth]';
import ServicePropsHomepage from '../src/server/models/game/services/hydrators/homepage';
import Game from '../src/models/game';
import HomeNews from '@/components/home/news';
import type { ApiNewsPost } from '@/src/types/apinews';

export const getServerSideProps = (async (context) => {
    const { req, res } = context;
    const session = await getServerSession(req, res, authOptions);

    if (!session) {
        return {
            props: {},
        };
    }

    const userId = session.user.id;
    const fromServer = await ServicePropsHomepage(userId);

    return {
        props: fromServer,
    };
});

export default function Homepage(
    {
        newsPosts = [],
    }: {
        newsPosts?: ApiNewsPost[];
    },
): JSX.Element {
    const { data: session, status } = useSession();
    const router = useRouter();
    const { activeGames: allActiveGames, openGames } = useActiveGamesProvider();

    const onRefresh = useCallback(
        () => {
            router.reload();
        },
        [router],
    );

    useEffect(
        () => {
            if (router.query.error) {
                console.error(router.query.error);
            }
        },
        [router],
    );

    if (status === 'loading') {
        return <><Title subtitle="Loading..." /><Spinner /></>;
    } else if (status === 'unauthenticated') {
        return <NotLoggedIn />;
    }

    const userId = session.user.id;
    const activeGames = allActiveGames.filter(
        game => game.activeSeats.some(
            seat => seat.userId === userId
        ) || game.invites?.find(
            invite => invite.user?.id === userId
        ) || (new Game(game)).isGameOverAndUnseen(userId),
    );

    return (
        <div id="content">
            <PullToRefresh onRefresh={onRefresh}>
                <Title />
                <div>
                    <HomeGamesHeaderCard games={activeGames} />
                    <GameGamesList
                        games={activeGames}
                        userId={userId}
                    />
                    {
                        openGames?.length > 0 &&
                        <h1>Open Games:</h1>
                    }
                    <GameGamesList
                        games={openGames}
                        userId={userId}
                    />
                    <HomeLiveGames />
                    <HomeNews newsPosts={newsPosts} />
                </div>
            </PullToRefresh>
        </div>
    );
}
